<!-- Spotlight-style hint tooltip anchored to a real UI element. -->
<!-- Renders a full-screen dim with a cutout around the target, plus a hint card positioned nearby. -->
<template>
    <Teleport to="body">
        <template v-if="spotlightRect && currentStep">
            <div class="hint-overlay">
                <div class="hint-spotlight" :style="spotlightStyle" />
            </div>
            <div class="hint-card" :style="cardStyle">
                <div class="hint-header">
                    <span class="hint-badge">Hint</span>
                    <Button
                        icon="pi pi-times"
                        text
                        rounded
                        severity="secondary"
                        size="small"
                        aria-label="Disable all hints"
                        class="hint-close"
                        @click="onDismissAll"
                    />
                </div>
                <h4 class="hint-title">{{ currentStep.title }}</h4>
                <p class="hint-content">{{ currentStep.content }}</p>
                <div class="hint-footer">
                    <Button
                        label="Disable hints"
                        severity="secondary"
                        size="small"
                        text
                        @click="onDismissAll"
                    />
                    <Button
                        :label="isLastStep ? 'Done' : 'Next →'"
                        severity="primary"
                        size="small"
                        @click="onNext"
                    />
                </div>
            </div>
        </template>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";

import type { HintStep } from "@/composables/use-page-hints";

const SPOTLIGHT_PADDING = 8;
const CARD_GAP = 12;
const CARD_WIDTH = 280;
const CARD_ESTIMATED_HEIGHT = 160;
const CARD_SCREEN_MARGIN = 16;

const props = defineProps<{
    currentStep: HintStep | null;
    isLastStep: boolean;
}>();

const emit = defineEmits<{
    next: [];
    dismissAll: [];
}>();

const spotlightRect = ref<DOMRect | null>(null);

const getTargetEl = (): HTMLElement | null => {
    const raw = props.currentStep?.targetRef.value;
    if (!raw) return null;
    // ref on a Vue component returns the instance; unwrap to the real DOM element
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (raw as any).$el ?? raw;
};

const updateSpotlightRect = () => {
    const target = getTargetEl();
    if (target) spotlightRect.value = target.getBoundingClientRect();
};

const showOnTarget = () => {
    const target = getTargetEl();
    if (!target) return;
    updateSpotlightRect();
    window.addEventListener("scroll", updateSpotlightRect, {
        passive: true,
        capture: true,
    });
    window.addEventListener("resize", updateSpotlightRect, { passive: true });
};

const hideHint = () => {
    spotlightRect.value = null;
    window.removeEventListener("scroll", updateSpotlightRect, {
        capture: true,
    });
    window.removeEventListener("resize", updateSpotlightRect);
};

const spotlightStyle = computed(() => {
    if (!spotlightRect.value) return {};
    return {
        top: `${spotlightRect.value.top - SPOTLIGHT_PADDING}px`,
        left: `${spotlightRect.value.left - SPOTLIGHT_PADDING}px`,
        width: `${spotlightRect.value.width + SPOTLIGHT_PADDING * 2}px`,
        height: `${spotlightRect.value.height + SPOTLIGHT_PADDING * 2}px`,
    };
});

const cardStyle = computed(() => {
    if (!spotlightRect.value) return {};
    const rect = spotlightRect.value;
    const spBottom = rect.bottom + SPOTLIGHT_PADDING;
    const spTop = rect.top - SPOTLIGHT_PADDING;

    // Prefer placing the card below the spotlight; fall back to above if it would clip.
    const top =
        spBottom + CARD_GAP + CARD_ESTIMATED_HEIGHT > window.innerHeight
            ? spTop - CARD_GAP - CARD_ESTIMATED_HEIGHT
            : spBottom + CARD_GAP;

    // Align left edge of card with spotlight, clamped to screen margins.
    const rawLeft = rect.left - SPOTLIGHT_PADDING;
    const left = Math.max(
        CARD_SCREEN_MARGIN,
        Math.min(rawLeft, window.innerWidth - CARD_WIDTH - CARD_SCREEN_MARGIN)
    );

    return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${CARD_WIDTH}px`,
    };
});

watch(
    () => props.currentStep,
    (step) => {
        if (step) {
            // Delay to allow the target element to be mounted/visible
            setTimeout(showOnTarget, 100);
        } else {
            hideHint();
        }
    },
    { immediate: true }
);

// Retry showing when the target element mounts after currentStep is already set.
// This handles conditionally-rendered targets (e.g. behind a v-if) that aren't
// in the DOM when the step first becomes active.
watch(
    () => props.currentStep?.targetRef.value,
    (target) => {
        if (target && props.currentStep) {
            setTimeout(showOnTarget, 100);
        }
    }
);

const onNext = () => {
    hideHint();
    emit("next");
};

const onDismissAll = () => {
    hideHint();
    emit("dismissAll");
};

onUnmounted(hideHint);
</script>

<style scoped>
.hint-overlay {
    position: fixed;
    inset: 0;
    /* Below the menubar (z-index 1000) so elements in the toolbar remain visible above the dim */
    z-index: 990;
    pointer-events: none;
}

.hint-spotlight {
    position: absolute;
    border-radius: 6px;
    /* box-shadow spreads out to cover the entire viewport outside the spotlight area */
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
    pointer-events: none;
}

.hint-card {
    position: fixed;
    z-index: 1001;
    background: var(--p-overlay-popover-background);
    border: 1px solid var(--p-overlay-popover-border-color);
    border-radius: var(--p-overlay-popover-border-radius);
    box-shadow: var(--p-overlay-popover-shadow);
    padding: 0.75rem 0.75rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.hint-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.hint-badge {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--p-primary-color);
    background: var(--p-primary-50);
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
}

.hint-close {
    margin-left: auto;
}

.hint-title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
}

.hint-content {
    margin: 0;
    font-size: 0.85rem;
    color: var(--p-text-muted-color);
    line-height: 1.4;
}

.hint-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.25rem;
}
</style>
